import { Link } from "react-router-dom";
import { Check, Info, ArrowRight } from "lucide-react";
import PriceCalculator from "@/components/PriceCalculator";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";

const priceRows = [
  { service: "Hækklipning (standardhøjde 1,5 – 2,5 m)", price: "32 kr/m", note: "Inkl. bortkørsel og oprydning" },
  { service: "Hækklipning (over 2,5 m)", price: "fra 42 kr/m", note: "Kræver stige/lift-vurdering" },
  { service: "Topbeskæring", price: "+8 kr/m", note: "Ekstra tillæg ved bestilling" },
  { service: "Formklipning (fri form / kugle / kegle)", price: "+12 kr/m", note: "Ekstra tillæg" },
  { service: "Stubfræsning", price: "fra 450 kr/stub", note: "Efter besigtigelse" },
  { service: "Græsslåning (fast aftale)", price: "fra 350 kr/gang", note: "Inkl. opsamling" },
  { service: "Løvblæsning & oprydning", price: "fra 450 kr", note: "Efter havestørrelse" },
];

const faqs = [
  {
    q: "Hvad er inkluderet i prisen på 32 kr/m?",
    a: "Prisen dækker selve klipningen, bortkørsel af haveaffald samt oprydning på arbejdsområdet. Der er ingen skjulte gebyrer — hvad du ser i beregneren er hvad du betaler.",
  },
  {
    q: "Hvor lang tid tager en hækklipning?",
    a: "En standardhæk på 20-30 meter tager typisk 1-2 timer. Vi aftaler altid tid på forhånd, og vi rydder op inden vi kører.",
  },
  {
    q: "Hvornår er det bedst at klippe hæk?",
    a: "For de fleste hække er slutningen af juni eller august ideelt. Vi rådgiver gerne om det bedste tidspunkt for netop din hæktype.",
  },
  {
    q: "Skal jeg selv være hjemme?",
    a: "Nej — du behøver ikke være hjemme. Vi aftaler adgang og arbejdsområde, og du får en besked når vi er færdige.",
  },
  {
    q: "Kan jeg betale med MobilePay?",
    a: "Ja, vi modtager MobilePay, bankoverførsel og faktura. Du får kvittering på mail.",
  },
  {
    q: "Hvad hvis jeg er utilfreds med resultatet?",
    a: "Så laver vi det om — uden ekstra beregning. Vi er ikke færdige før du er tilfreds.",
  },
];

export default function Pricing() {
  return (
    <div data-testid="page-pricing">
      <section className="mx-auto max-w-7xl px-6 lg:px-10 pt-16 pb-12">
        <div className="max-w-3xl">
          <div className="text-xs uppercase tracking-[0.22em] text-brand-muted mb-4">Priser</div>
          <h1 className="font-display text-5xl sm:text-6xl tracking-tight text-brand-text text-balance">
            Ærlige priser — <span className="italic text-brand-primary">ingen overraskelser</span>.
          </h1>
          <p className="mt-6 text-lg text-brand-muted leading-relaxed">
            Vi arbejder med faste priser — så du ved præcis hvad du får og hvad det koster, allerede før vi går i gang.
          </p>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-6 lg:px-10 pb-20">
        <PriceCalculator />
      </section>

      <section className="mx-auto max-w-7xl px-6 lg:px-10 pb-20">
        <div className="text-xs uppercase tracking-[0.22em] text-brand-muted mb-3">Prisliste</div>
        <h2 className="font-display text-4xl sm:text-5xl tracking-tight text-brand-text mb-10">
          Standardpriser
        </h2>
        <div className="rounded-3xl overflow-hidden border border-brand-border bg-brand-surface">
          <table className="w-full">
            <thead className="bg-brand-soft/60 border-b border-brand-border">
              <tr>
                <th className="text-left py-4 px-6 text-xs uppercase tracking-[0.18em] text-brand-muted font-medium">Ydelse</th>
                <th className="text-left py-4 px-6 text-xs uppercase tracking-[0.18em] text-brand-muted font-medium">Pris</th>
                <th className="text-left py-4 px-6 text-xs uppercase tracking-[0.18em] text-brand-muted font-medium hidden sm:table-cell">Bemærkning</th>
              </tr>
            </thead>
            <tbody>
              {priceRows.map((r, i) => (
                <tr
                  key={i}
                  data-testid={`price-row-${i}`}
                  className={i !== priceRows.length - 1 ? "border-b border-brand-border/60" : ""}
                >
                  <td className="py-4 px-6 text-brand-text">{r.service}</td>
                  <td className="py-4 px-6 font-medium text-brand-primary whitespace-nowrap">{r.price}</td>
                  <td className="py-4 px-6 text-sm text-brand-muted hidden sm:table-cell">{r.note}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="mt-4 flex items-start gap-2 text-sm text-brand-muted">
          <Info className="h-4 w-4 mt-0.5 shrink-0" />
          <span>Alle priser er inkl. moms. Endelig pris efter besigtigelse eller vurdering af billeder.</span>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-6 lg:px-10 pb-24">
        <div className="grid lg:grid-cols-[1fr_1.4fr] gap-10 items-start">
          <div className="lg:sticky lg:top-28">
            <div className="text-xs uppercase tracking-[0.22em] text-brand-muted mb-3">FAQ</div>
            <h2 className="font-display text-4xl sm:text-5xl tracking-tight text-brand-text">
              Ofte stillede <span className="italic">spørgsmål</span>.
            </h2>
            <p className="mt-4 text-brand-muted">
              Har du et andet spørgsmål? Ring til os på {" "}
              <Link to="/kontakt" className="text-brand-primary underline underline-offset-4">Kontakt-siden</Link>.
            </p>
          </div>
          <Accordion type="single" collapsible className="w-full" data-testid="faq-accordion">
            {faqs.map((f, i) => (
              <AccordionItem key={i} value={`item-${i}`} className="border-b border-brand-border">
                <AccordionTrigger
                  data-testid={`faq-q-${i}`}
                  className="font-display text-lg text-brand-text text-left py-5 hover:text-brand-primary"
                >
                  {f.q}
                </AccordionTrigger>
                <AccordionContent className="text-brand-muted leading-relaxed pb-5">
                  {f.a}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>

        <div className="mt-14 text-center">
          <Link
            to="/kontakt"
            data-testid="pricing-cta-quote"
            className="inline-flex items-center gap-2 rounded-full bg-brand-primary text-brand-bg px-8 py-4 font-medium hover:bg-brand-hover"
          >
            Få et personligt tilbud <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      </section>
    </div>
  );
}
